import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { MudCarpFish } from "@/components/MudCarpFish";
import { Bubbles } from "@/components/Bubbles";
import { api, REGULATION_VERSION, type ApiError, type CheckoutRequest } from "@/lib/api";
import { useAuth, KICK_LOGIN_URL } from "@/lib/auth";
import { clampAmount, formatPln, reactionTier, TIER_COPY } from "@/lib/utils";

const PREORDER_THRESHOLD = 10000;

const PRESETS = [10, 25, 50, 100, 500, 1000] as const;
const MAX_MSG = 500;

const CHANNELS = [
  { id: "card" as const, label: "Karta", hint: "Visa, Mastercard, AmEx" },
  { id: "blik" as const, label: "BLIK", hint: "6 cyfr i klepiesz" },
  { id: "p24" as const, label: "Przelewy24", hint: "Przelew na żywo" },
];

type FormState = {
  amount: number;
  email: string;
  customerName: string;
  message: string;
  paymentChannel: "card" | "blik" | "p24";
  regulationAccepted: boolean;
  digitalDeliveryConsent: boolean;
};

type Errors = Partial<Record<keyof FormState, string>>;

export function Buy() {
  const reduced = useReducedMotion();
  const [searchParams] = useSearchParams();
  const initialAmount = useMemo(() => {
    const fromUrl = parseFloat(searchParams.get("amount") || "");
    if (!Number.isNaN(fromUrl)) return clampAmount(fromUrl);
    return 50;
  }, [searchParams]);

  const [form, setForm] = useState<FormState>({
    amount: initialAmount,
    email: "",
    customerName: "",
    message: "",
    paymentChannel: "card",
    regulationAccepted: false,
    digitalDeliveryConsent: false,
  });
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);
  const [serverErr, setServerErr] = useState<string | null>(null);
  const [shake, setShake] = useState(false);

  // When signed in via Kick, email + nick come from the Kick account and are
  // locked (readOnly below). Hard-override any pre-typed values — buyer can't
  // edit identity once authenticated; to change it they need to log out.
  const auth = useAuth();
  const authUser = auth.loading ? null : auth.user;
  useEffect(() => {
    if (!authUser) return;
    setForm((f) => ({
      ...f,
      email: authUser.email ?? f.email,
      customerName: authUser.kickUsername ?? f.customerName,
    }));
  }, [authUser]);

  const tier = reactionTier(form.amount);
  const tierCopy = TIER_COPY[tier];
  const isPreorder = form.amount >= PREORDER_THRESHOLD;

  // sync slider/preset/input
  function setAmount(value: number) {
    setForm((f) => ({ ...f, amount: clampAmount(value) }));
    setErrors((e) => ({ ...e, amount: undefined }));
  }

  function validate(): boolean {
    const e: Errors = {};
    if (form.amount < 5) e.amount = "Minimum to 5 zł, mordo.";
    if (form.amount > 10000) e.amount = "Maksimum to 10 000 zł. Karp ma sumienie.";
    if (!form.email) e.email = "Mail jest potrzebny — choćby na fakturę.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "To nie wygląda na mail.";
    if (!form.message.trim()) e.message = "No coś napisz, Karp czeka.";
    if (form.message.length > MAX_MSG) e.message = `Max ${MAX_MSG} znaków. Karp jest stary, dużo nie pamięta.`;
    if (!form.regulationAccepted) e.regulationAccepted = "Trzeba kliknąć tę kratkę.";
    if (!form.digitalDeliveryConsent) e.digitalDeliveryConsent = "Bez tej zgody nie sprzedamy.";
    setErrors(e);
    if (Object.keys(e).length) {
      setShake(true);
      setTimeout(() => setShake(false), 450);
    }
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    setServerErr(null);
    if (!validate()) return;

    const body: CheckoutRequest = {
      amount: form.amount,
      currency: "PLN",
      email: form.email.trim(),
      customerName: form.customerName.trim() || null,
      message: form.message.trim(),
      paymentChannel: form.paymentChannel,
      regulationAccepted: true,
      digitalDeliveryConsent: true,
      regulationVersion: REGULATION_VERSION,
    };

    setSubmitting(true);
    try {
      const res = await api.checkout(body);
      window.location.href = res.redirectUrl;
    } catch (raw) {
      const err = raw as ApiError;
      setServerErr(err.message || "Coś się skiepściło. Spróbuj jeszcze raz.");
      // map field errors back if any
      if (err.errors?.length) {
        const mapped: Errors = {};
        for (const e of err.errors) {
          const f = (e.field || "").toLowerCase();
          if (f.includes("amount")) mapped.amount = e.message;
          else if (f.includes("email")) mapped.email = e.message;
          else if (f.includes("message")) mapped.message = e.message;
          else if (f.includes("channel")) mapped.paymentChannel = e.message;
          else if (f.includes("regulation")) mapped.regulationAccepted = e.message;
          else if (f.includes("delivery")) mapped.digitalDeliveryConsent = e.message;
        }
        setErrors(mapped);
        setShake(true);
        setTimeout(() => setShake(false), 450);
      }
      setSubmitting(false);
    }
  }

  return (
    <div className="relative">
      <Bubbles count={10} />

      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14 grid lg:grid-cols-12 gap-10">
        {/* LEFT — fish + reaction */}
        <aside className="lg:col-span-5 lg:sticky lg:top-24 self-start">
          <div className="brut bg-carp-paper p-6 -rotate-1">
            <div className="flex items-baseline justify-between mb-2">
              <span className="chip">REAKCJA KARPIA</span>
              <span className="font-mono text-xs text-carp-ink/60">tier {tier}/4</span>
            </div>
            <div className="aspect-[4/3] flex items-center justify-center">
              <MudCarpFish intensity={tier} size={0.85} talking={tier >= 3} ariaHidden={false} />
            </div>
            <AnimatePresence mode="wait">
              <motion.div
                key={tier}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.2 }}
                className="mt-2"
              >
                <p className="font-display font-bold text-3xl tracking-tight">{tierCopy.tag}</p>
                <p className="text-base text-carp-ink/80 leading-snug">
                  Karp <strong>{tierCopy.mood}</strong>. Stan: {tierCopy.emojiless}.
                </p>
              </motion.div>
            </AnimatePresence>

            <div className="mt-5 brut bg-carp-yellow p-3">
              <p className="font-display font-bold text-xl leading-tight">
                Wpłacasz: {formatPln(form.amount)}
              </p>
              <p className="text-xs text-carp-ink/70 font-mono">
                {form.message.length}/{MAX_MSG} znaków · kanał {form.paymentChannel.toUpperCase()}
              </p>
            </div>
          </div>

          <div className="mt-4 brut bg-carp-ink text-carp-paper p-4 rotate-1">
            <p className="font-mono text-xs uppercase tracking-wide text-carp-yellow">cytat na dziś</p>
            <p className="mt-1 font-display text-lg leading-tight">
              „Karp nie pyta jak głęboko, tylko jak dalej."
            </p>
          </div>
        </aside>

        {/* RIGHT — form */}
        <motion.form
          onSubmit={handleSubmit}
          animate={shake && !reduced ? { x: [-6, 6, -4, 4, 0] } : { x: 0 }}
          transition={{ duration: 0.4 }}
          className="lg:col-span-7 brut bg-carp-paper p-6 sm:p-8 space-y-8"
          noValidate
        >
          <header>
            <p className="chip mb-2">FORMULARZ ZAGADANIA</p>
            <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">
              No to gadaj <span className="text-carp-primary">do karpia</span>.
            </h1>
            <p className="mt-2 text-carp-ink/80">
              Trzy rzeczy: ile, co, jak płacisz. Zatwierdzisz regulamin i lecimy do Stripe.
            </p>
          </header>

          {/* PREORDER 10K BANNER — pojawia się gdy amount >= 10000 */}
          <AnimatePresence>
            {isPreorder && (
              <motion.div
                initial={{ opacity: 0, y: -8, height: 0 }}
                animate={{ opacity: 1, y: 0, height: "auto" }}
                exit={{ opacity: 0, y: -8, height: 0 }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden"
              >
                <div className="brut bg-carp-yellow p-4 sm:p-5 relative">
                  <div className="flex items-start gap-4">
                    <div aria-hidden className="hidden sm:block shrink-0 w-16 -mt-2">
                      <MudCarpFish intensity={4} size={0.18} talking />
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="chip bg-carp-ink text-carp-yellow">
                          Przedsprzedaż
                        </span>
                        <span className="chip bg-carp-primary text-white">
                          10 000 zł = fizyczny karp
                        </span>
                      </div>
                      <p className="mt-2 font-display font-bold text-xl sm:text-2xl leading-tight text-balance">
                        Płacisz dychę kafla — dostajesz MudCarp Device + roczna subskrypcja odpowiedzi.
                      </p>
                      <p className="mt-1 text-sm sm:text-base leading-snug">
                        Oficjalny release{" "}
                        <strong className="text-carp-primary">1 września 2026</strong> —
                        w sam raz na powrót do szkoły. Karp w pokoju ucznia robi za korepetytora,
                        który mówi prawdę w oczy. Subskrypcja startuje w dniu releasu, kurier do dwóch tygodni przed.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* AMOUNT */}
          <fieldset className="space-y-3">
            <legend className="label-brut">1. Ile dajesz, mordo?</legend>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={5}
                  max={10000}
                  step="0.01"
                  inputMode="decimal"
                  value={form.amount}
                  onChange={(ev) => setAmount(parseFloat(ev.target.value))}
                  aria-invalid={!!errors.amount}
                  aria-describedby={errors.amount ? "amount-err" : "amount-hint"}
                  className="input-brut w-40 font-display font-bold text-2xl"
                />
                <span className="font-display font-bold text-2xl">PLN</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {PRESETS.map((p) => {
                  const active = Math.abs(form.amount - p) < 0.01;
                  return (
                    <button
                      type="button"
                      key={p}
                      onClick={() => setAmount(p)}
                      className={`btn-ghost text-sm ${active ? "bg-carp-yellow" : ""}`}
                    >
                      {p}
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => setAmount(PREORDER_THRESHOLD)}
                  aria-label="Przedsprzedaż MudCarp Device — 10 000 PLN"
                  className={`btn-ghost text-sm font-bold !border-carp-primary ${
                    form.amount >= PREORDER_THRESHOLD
                      ? "bg-carp-primary text-white"
                      : "bg-carp-paper text-carp-primary"
                  }`}
                >
                  10 000 — przedsprzedaż
                </button>
              </div>
            </div>
            <input
              type="range"
              min={5}
              max={10000}
              step={1}
              value={form.amount}
              onChange={(ev) => setAmount(parseInt(ev.target.value))}
              aria-label="Suwak kwoty"
              className="w-full accent-carp-primary cursor-pointer"
            />
            <p
              id={errors.amount ? "amount-err" : "amount-hint"}
              role={errors.amount ? "alert" : undefined}
              className={`text-sm ${errors.amount ? "text-carp-destructive font-bold" : "text-carp-ink/70"}`}
            >
              {errors.amount ?? "Min 5 zł, max 10 000 zł. Karp wszystko zliczy."}
            </p>
          </fieldset>

          {/* MESSAGE */}
          <fieldset className="space-y-2">
            <legend className="label-brut" id="message-label">
              2. Co ma palnąć?
            </legend>
            <textarea
              id="message"
              aria-labelledby="message-label"
              aria-invalid={!!errors.message}
              aria-describedby="message-counter"
              value={form.message}
              onChange={(ev) => {
                const v = ev.target.value.slice(0, MAX_MSG + 50);
                setForm((f) => ({ ...f, message: v }));
                setErrors((e) => ({ ...e, message: undefined }));
              }}
              rows={4}
              maxLength={MAX_MSG + 50}
              placeholder="np. Powiedz Krzysiowi że za 5 lat ciągle nie umie tabliczki mnożenia."
              className="input-brut font-body text-base leading-relaxed resize-none min-h-[120px]"
            />
            <div className="flex items-center justify-between text-sm">
              <span
                id="message-counter"
                className={`font-mono ${form.message.length > MAX_MSG ? "text-carp-destructive font-bold" : "text-carp-ink/70"}`}
              >
                {form.message.length}/{MAX_MSG}
              </span>
              {errors.message && (
                <span role="alert" className="text-carp-destructive font-bold">
                  {errors.message}
                </span>
              )}
            </div>
          </fieldset>

          {/* CONTACT */}
          <fieldset className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="email" className="label-brut">
                3a. Mail
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                inputMode="email"
                value={form.email}
                onChange={(ev) => {
                  setForm((f) => ({ ...f, email: ev.target.value }));
                  setErrors((e) => ({ ...e, email: undefined }));
                }}
                readOnly={!!authUser}
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? "email-err" : "email-hint"}
                placeholder="ty@nieprzecietny.pl"
                className={`input-brut ${authUser ? "bg-carp-muted/60 cursor-not-allowed" : ""}`}
              />
              <p
                id={errors.email ? "email-err" : "email-hint"}
                role={errors.email ? "alert" : undefined}
                className={`mt-1 text-xs ${errors.email ? "text-carp-destructive font-bold" : "text-carp-ink/60"}`}
              >
                {errors.email ?? (authUser ? "Z konta Kick — wyloguj, żeby zmienić." : "Na fakturę i potwierdzenie.")}
              </p>
            </div>

            <div>
              <label htmlFor="customerName" className="label-brut">
                3b. Imię (opcjonalnie)
              </label>
              <div className="flex gap-2">
                <input
                  id="customerName"
                  type="text"
                  autoComplete="given-name"
                  value={form.customerName}
                  onChange={(ev) =>
                    setForm((f) => ({ ...f, customerName: ev.target.value }))
                  }
                  readOnly={!!authUser}
                  placeholder="Jak Karp ma się do ciebie zwracać"
                  className={`input-brut flex-1 ${authUser ? "bg-carp-muted/60 cursor-not-allowed" : ""}`}
                />
                {!authUser && (
                  <a
                    href={KICK_LOGIN_URL}
                    className="btn-ghost shrink-0 whitespace-nowrap"
                    title="Zaloguj się przez Kick, żeby podstawić nick automatycznie"
                  >
                    Zaloguj z Kick
                  </a>
                )}
              </div>
              <p className="mt-1 text-xs text-carp-ink/60">
                {authUser
                  ? `Zalogowany jako ${authUser.kickUsername ?? "Kick user"} — wyloguj, żeby zmienić.`
                  : 'Bez tego Karp powie „Anonimusie” — i tak ujdzie.'}
              </p>
            </div>
          </fieldset>

          {/* PAYMENT CHANNEL */}
          <fieldset>
            <legend className="label-brut">4. Czym płacisz?</legend>
            <div role="radiogroup" className="grid sm:grid-cols-3 gap-3">
              {CHANNELS.map((ch) => {
                const active = form.paymentChannel === ch.id;
                return (
                  <label
                    key={ch.id}
                    className={`brut cursor-pointer p-4 select-none ${active ? "bg-carp-accent text-white" : "bg-white hover:bg-carp-yellow/40"}`}
                  >
                    <input
                      type="radio"
                      name="paymentChannel"
                      value={ch.id}
                      checked={active}
                      onChange={() => setForm((f) => ({ ...f, paymentChannel: ch.id }))}
                      className="sr-only"
                    />
                    <div className="font-display font-bold text-xl">{ch.label}</div>
                    <div className={`text-xs ${active ? "text-white/85" : "text-carp-ink/65"}`}>{ch.hint}</div>
                  </label>
                );
              })}
            </div>
          </fieldset>

          {/* CONSENT */}
          <fieldset className="space-y-4">
            <legend className="label-brut">5. Małym drukiem (no, średnim)</legend>
            <Consent
              id="reg"
              checked={form.regulationAccepted}
              onChange={(v) => {
                setForm((f) => ({ ...f, regulationAccepted: v }));
                setErrors((e) => ({ ...e, regulationAccepted: undefined }));
              }}
              error={errors.regulationAccepted}
            >
              Zapoznałem się z{" "}
              <Link to="/regulamin" target="_blank" className="link-brut">
                regulaminem {REGULATION_VERSION}
              </Link>{" "}
              oraz{" "}
              <Link to="/polityka-prywatnosci" target="_blank" className="link-brut">
                polityką prywatności
              </Link>{" "}
              i wiem że <strong>nie ma zwrotów</strong>. Karp powie i to się nie odstanie.
            </Consent>
            <Consent
              id="dlv"
              checked={form.digitalDeliveryConsent}
              onChange={(v) => {
                setForm((f) => ({ ...f, digitalDeliveryConsent: v }));
                setErrors((e) => ({ ...e, digitalDeliveryConsent: undefined }));
              }}
              error={errors.digitalDeliveryConsent}
            >
              Zgadzam się żeby Karp zaczął gadać <strong>od razu</strong> po zapłacie i tym samym tracę prawo odstąpienia 14 dni —
              {" "}<em className="not-italic underline decoration-carp-yellow decoration-4 underline-offset-2">art. 38 ust. 13 ustawy o prawach konsumenta</em>{" "}
              (taka mała czcionka, żeby było legalnie).
            </Consent>
          </fieldset>

          {/* SERVER ERR */}
          <AnimatePresence>
            {serverErr && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                role="alert"
                className="brut bg-carp-destructive text-white p-4"
              >
                <p className="font-display font-bold uppercase">Karp się zawiesił</p>
                <p className="text-sm">{serverErr}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* SUBMIT */}
          <div className="flex flex-wrap items-center gap-4 pt-2">
            <SubmitButton submitting={submitting} amount={form.amount} />
            <p className="text-xs text-carp-ink/60 font-mono max-w-xs">
              Po kliknięciu lecisz do Stripe — tam płacisz, wracasz, słuchasz.
            </p>
          </div>
        </motion.form>
      </section>
    </div>
  );
}

function Consent({
  id,
  checked,
  onChange,
  error,
  children,
}: {
  id: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className={`flex items-start gap-3 cursor-pointer p-3 border-[3px] border-carp-ink ${checked ? "bg-carp-yellow" : "bg-white"} hover:bg-carp-yellow/40 transition-colors`}
      >
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(ev) => onChange(ev.target.checked)}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-err` : undefined}
          className="mt-1 w-5 h-5 accent-carp-primary cursor-pointer flex-shrink-0"
        />
        <span className="text-sm leading-snug select-none">{children}</span>
      </label>
      {error && (
        <p id={`${id}-err`} role="alert" className="mt-1 text-sm text-carp-destructive font-bold">
          {error}
        </p>
      )}
    </div>
  );
}

function SubmitButton({ submitting, amount }: { submitting: boolean; amount: number }) {
  const reduced = useReducedMotion();
  const [tick, setTick] = useState(0);
  const phrases = useMemo(
    () => [
      "Karp wciąga powietrze",
      "Stripe drukuje sesję",
      "BLIK podgrzewa się",
      "Karp prostuje płetwę",
      "Karp odchrząkuje",
    ],
    [],
  );

  useEffect(() => {
    if (!submitting) return;
    const i = window.setInterval(() => setTick((t) => t + 1), 900);
    return () => window.clearInterval(i);
  }, [submitting]);

  return (
    <button
      type="submit"
      disabled={submitting}
      className="btn-primary text-lg disabled:opacity-80 disabled:cursor-wait min-w-[260px]"
      style={reduced ? undefined : { animation: submitting ? "none" : "pulseGlow 2.4s infinite" }}
    >
      {submitting ? (
        <span className="inline-flex items-center gap-3">
          <Spinner />
          {phrases[tick % phrases.length]}…
        </span>
      ) : (
        <>
          Płacę {formatPln(amount)} i czekam aż Karp zacznie
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </>
      )}
    </button>
  );
}

function Spinner() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" className="animate-spin" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" fill="none" opacity="0.25" />
      <path
        d="M21 12a9 9 0 0 1-9 9"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

export default Buy;
